import { Button, CircularProgress, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import type { FC } from 'react';
import { Checkbox, ConfirmationDialog, SelectApplications, SelectPlatform, TextField } from 'components/common';
import { createAddAccountValidator } from '../validation';
import type { z } from 'zod';
import type { AddAccountPayload } from '../api/addAccount';
import { useAddAccount, useUpdateAccount } from '../api/addAccount';
import type { Account } from '../types';
import { useNavigate } from 'react-router';
import { useEnqueueSnackbar } from 'hooks';
import { isEmpty } from 'lodash-es';
import {
  createCabinetMutationTracking,
  trackCabinetSubmitAttempt,
} from 'lib/datadog';

interface AccountFormProps {
  data?: Account;
}

const AccountForm: FC<AccountFormProps> = ({ data }) => {
  const { onShowErrorAlert, onShowAlert } = useEnqueueSnackbar();
  const navigate = useNavigate();
  const isEditMode = !isEmpty(data);

  const addAccount = useAddAccount({
    config: createCabinetMutationTracking({
      platform: 'facebook',
      entity: 'account',
      mode: 'create',
      onSuccess: () => {
        onShowAlert('Account added successfully');
        navigate('/cabinet/facebook/accounts');
      },
      onError: (error) => {
        onShowErrorAlert(error.message);
      },
    }),
  });

  const updateAccount = useUpdateAccount({
    config: createCabinetMutationTracking({
      platform: 'facebook',
      entity: 'account',
      mode: 'edit',
      onSuccess: () => {
        onShowAlert('Account updated successfully');
        navigate('/cabinet/facebook/accounts');
      },
      onError: (error) => {
        onShowErrorAlert(error.message);
      },
    }),
  });

  const validator = createAddAccountValidator();
  type FormData = z.infer<typeof validator.schema>;
  const formMethods = useForm<FormData>({
    resolver: validator.resolver,
    defaultValues: {
      adAccountId: data?.ad_account_id ?? '',
      name: data?.name ?? '',
      platform: data?.platform ?? 'WEB',
      app: data?.app ?? '',
      isActive: data?.is_active ?? true,
      ciUpload: data?.ci_upload ?? true,
      blacklistChecks: data?.blacklist_check ?? true,
    },
  });

  const { handleSubmit, formState: { isValid } } = formMethods;

  const onSubmit = (formData: FormData) => {
    trackCabinetSubmitAttempt(
      'facebook',
      'account',
      isEditMode ? 'edit' : 'create',
    );

    const payload: AddAccountPayload = {
      ad_account_id: formData?.adAccountId,
      ad_account_name: formData?.name,
      platform: formData?.platform,
      app: formData?.app,
      ci_upload: formData?.ciUpload,
      is_active: formData?.isActive,
      blacklist_check: formData?.blacklistChecks,
    };
    if (isEditMode && data) {
      updateAccount.mutate({
        ...payload,
        id: data.id,
      });
    } else {
      addAccount.mutate(payload);
    }
  };

  const isPending = isEditMode ? updateAccount.isPending : addAccount.isPending;

  return (
    <>
      <FormProvider {...formMethods}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            maxWidth: '40%',
            alignItems: 'start',
            justifyContent: 'start',
          }}
        >
          <Typography variant={'subtitle1'}>
            {isEditMode ? 'Edit account' : 'Add account'}
          </Typography>
          <TextField
            name={'adAccountId'}
            inputLabel={'Ad account ID'}
            placeholder={'Value'}
            inputProps={{ autoFocus: true }}
          />
          <TextField
            name={'name'}
            inputLabel={'Ad account name'}
            placeholder={'Value'}
          />
          <SelectApplications />
          <SelectPlatform />
          <Checkbox
            name={'isActive'}
            label={'is Active'}
            controlLabelSx={{ whiteSpace: 'nowrap' }}
          />
          <Checkbox
            name={'ciUpload'}
            label={'Creative media uploads'}
            controlLabelSx={{ whiteSpace: 'nowrap' }}
          />
          <Checkbox
            name={'blacklistChecks'}
            label={'Blacklist Checks'}
            controlLabelSx={{ whiteSpace: 'nowrap' }}
          />

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Button
              startIcon={
                isPending && <CircularProgress color={'inherit'} size={18} />
              }
              onClick={handleSubmit(onSubmit)}
              variant={'contained'}
              disabled={isPending || !isValid}
            >
              {isEditMode ? 'Update account' : 'Save account'}
            </Button>
          </Box>
        </Box>
        <ConfirmationDialog isUnblock={addAccount.isSuccess || updateAccount.isSuccess} />
      </FormProvider>
    </>
  );
};

export default AccountForm;
